import { ChangeEvent, ChangeEventHandler, FocusEventHandler } from 'react';

import {
  Box,
  Button,
  Drawer,
  IconButton,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { FormikErrors } from 'formik';

import PreviewDrawer from '../PreviewDrawer';
import Duration from '../Duration';
import Switch from '../Switch';

import {
  AllergyFormData,
  TAllergyInitialValues,
} from '../../../pages/allergies/create/types';
import { FormFieldData } from '../../../constants/types';
import {
  MedicationFormData,
  TDosageFrequency,
  TMedicationInitialValues,
  TSubComponent,
} from 'pages/medications/addMedication/types';

import {
  ArrowLeftIcon,
  CloseIcon,
  PillIcon,
  SyringeIcon,
} from '../CustomIcons';
import Select from './Select';

import { Checkbox, FormHelperText } from '@mui/material';

import LightModeIcon from '@mui/icons-material/LightMode';
import Brightness5Icon from '@mui/icons-material/Brightness5';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import Brightness5OutlinedIcon from '@mui/icons-material/Brightness5Outlined';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';

type FormValues = TAllergyInitialValues | TMedicationInitialValues;

interface IFormCreatorComponentProps {
  index: number;
  isModalOpen: boolean;
  isValid: boolean;
  values: AllergyFormData | MedicationFormData;
  dirty: boolean;
  formFieldData: FormFieldData;
  isFieldTouched: boolean;
  errors: FormikErrors<FormValues>;
  handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleSubmit: () => void;
  handleBack: () => void;
  handleClose: () => void;
  handleContinue: () => void;
  handleSelectChange: (event: SelectChangeEvent) => void;
  handleDateChange: (date: Dayjs | null) => void;
  handleSwitch: (event: ChangeEvent<HTMLInputElement>) => void;
  handleCheckBoxChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const dosageTimes = [
  {
    name: 'morning',
    label: 'Morning',
    icon: LightModeOutlinedIcon,
    checkedIcon: LightModeIcon,
  },
  {
    name: 'afternoon',
    label: 'Afternoon',
    icon: Brightness5OutlinedIcon,
    checkedIcon: Brightness5Icon,
  },
  {
    name: 'evening',
    label: 'Evening',
    icon: NightsStayOutlinedIcon,
    checkedIcon: NightsStayIcon,
  },
];

const FormCreatorComponent: React.FC<IFormCreatorComponentProps> = ({
  index,
  isModalOpen,
  isValid,
  dirty,
  formFieldData,
  values,
  isFieldTouched,
  errors,
  handleBlur,
  handleChange,
  handleSubmit,
  handleBack,
  handleClose,
  handleContinue,
  handleSelectChange,
  handleSwitch,
  handleDateChange,
  handleCheckBoxChange,
}) => {
  const {
    id,
    name,
    inputType,
    placeholder,
    label,
    iconName,
    buttonName,
    subComponent,
  } = formFieldData;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const value =
    values[name as keyof AllergyFormData | keyof MedicationFormData];

  const renderInputField = () => {
    if (inputType === 'text') {
      return (
        <TextField
          id={id}
          name={name}
          value={value as string}
          onBlur={handleBlur}
          onChange={handleChange}
          type="text"
          placeholder={placeholder}
          fullWidth
          inputProps={{ 'aria-label': 'Without label' }}
          sx={{ mb: 2 }}
          onKeyDown={handleKeyDown}
        />
      );
    }
    if (inputType === 'date') {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            label={placeholder}
            name={name}
            className="w-100"
            format="DD/MM/YYYY"
            minDate={dayjs().add(1, 'day')}
            slotProps={{
              field: { clearable: true },
            }}
            value={value ? dayjs(value) : null}
            onChange={(date) => handleDateChange(date)}
          />
        </LocalizationProvider>
      );
    }
    if (inputType === 'duration') {
      return (
        <Duration
          formData={values as TAllergyInitialValues}
          formErrors={errors}
          onInputChange={handleChange}
          onSelectChange={handleSelectChange}
        />
      );
    }
    if (inputType === 'select') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Select
            value={value as string}
            formFieldData={formFieldData}
            handleBlur={handleBlur}
            handleSelectChange={handleSelectChange}
            handleKeyDown={handleKeyDown}
          />

          {name === 'frequency' && value && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-around',
                gap: 2,
              }}
            >
              {dosageTimes.map(
                ({ name, label, icon: Icon, checkedIcon: CheckedIcon }) => (
                  <Box
                    key={name}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Checkbox
                      name={name}
                      checked={Boolean(
                        (values as TMedicationInitialValues)?.dosageFrequency?.[
                          name as keyof TDosageFrequency
                        ]
                      )}
                      icon={<Icon />}
                      checkedIcon={<CheckedIcon />}
                      onChange={handleCheckBoxChange}
                    />
                    <FormHelperText>{label}</FormHelperText>
                  </Box>
                )
              )}
            </Box>
          )}
        </Box>
      );
    }

    if (inputType === 'preview') return <PreviewDrawer values={values} />;

    return null;
  };

  const renderSubComponent = (component: TSubComponent) => {
    if (component.inputType === 'switch') {
      return (
        <Switch
          component={component}
          values={values}
          handleSwitch={handleSwitch}
        />
      );
    }

    return null;
  };

  return (
    <Drawer
      PaperProps={{
        sx: {
          minHeight: 640,
          width: {
            sm: 390,
          },
          m: '0 auto',
          boxShadow: 1,
          borderRadius: '24px 24px 0 0',
        },
      }}
      open={isModalOpen}
      anchor="bottom"
    >
      <form className="d-flex flex-column flex-1" onSubmit={handleSubmit}>
        <Box sx={{ p: 3 }} className="d-flex flex-column flex-1">
          <Box sx={{ gap: 2, mb: 3 }} className="d-flex">
            {index > 0 && (
              <IconButton
                aria-label="back"
                sx={{ color: 'text.primary', p: 0 }}
                onClick={handleBack}
              >
                <ArrowLeftIcon sx={{ fontSize: 40 }} />
              </IconButton>
            )}
            <IconButton
              aria-label="close"
              sx={{ color: 'text.primary', p: 0, ml: 'auto' }}
              onClick={handleClose}
            >
              <CloseIcon sx={{ fontSize: 40 }} />
            </IconButton>
          </Box>
          <Box
            sx={{ gap: 4, mb: 8 }}
            className="d-flex flex-column align-items-center"
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'paleBlue.main',
                color: 'tertiary.main',
                p: 1.5,
                borderRadius: 6.75,
              }}
              className="d-flex justify-content-center align-items-center"
            >
              {iconName === 'Injection' ? (
                <SyringeIcon sx={{ fontSize: 53 }} />
              ) : (
                <PillIcon sx={{ fontSize: 53 }} />
              )}
            </Box>
            <Typography variant="h5" sx={{ color: 'tertiary.main' }}>
              {label}
            </Typography>
          </Box>
          {renderInputField()}
          {subComponent?.map((component) => (
            <Box key={component.id}>{renderSubComponent(component)}</Box>
          ))}
          <Button
            disabled={!dirty || !isValid || !isFieldTouched}
            variant="contained"
            color="info"
            size="small"
            fullWidth
            sx={{ py: 2.5, mt: 'auto', mb: 4 }}
            onClick={handleContinue}
          >
            {buttonName}
          </Button>
        </Box>
      </form>
    </Drawer>
  );
};

export default FormCreatorComponent;
